import millify from "millify";
import InfoIcon from "../../../../assets/svgs/common/info.svg";
import { SheetTitle } from "../../../../components/ui/sheet";
import {
	Accordion,
	AccordionContent,
	AccordionItem,
	AccordionTrigger,
} from "../../../../components/ui/accordion";
import { Separator } from "../../../../components/ui/separator";
import {
	type IFavoriteMetrics,
	useGetMetricsAdminHttp,
} from "../../../../hooks/http/admin/favorites/metrics.admin.http";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { handleHttpError } from "../../../../utils/utils";
import { useToast } from "../../../../hooks/use-toast";
import Spinner from "../../../../components/spinner.components";
import GrowthIcon from "../../../../assets/svgs/common/growth.svg";

export default function StatsSliderMenuPartial() {
	const { favoriteId } = useParams();
	const { toast } = useToast();

	const { execute: getMetricsHttp, fetching: fetchingMetrics } =
		useGetMetricsAdminHttp();

	const [metircs, setMetrics] = useState<IFavoriteMetrics>();

	async function getMetrics() {
		if (!favoriteId) {
			return;
		}
		const response = await getMetricsHttp({
			body: null,
			pathParams: { favoriteId: favoriteId },
		});
		if (!response.success) {
			return handleHttpError(response, toast);
		}
		setMetrics(response.data.metrics);
	}
	// biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
	useEffect(() => {
		getMetrics();
	}, [favoriteId]);
	return (
		<div className="flex flex-col w-full">
			<SheetTitle className="pb-10 text-2xl text-white">Statistics</SheetTitle>
			{fetchingMetrics ? (
				<div className="flex flex-col justify-center items-center w-full h-96">
					<Spinner width={20} />
				</div>
			) : (
				<div className="flex flex-col items-center gap-8">
					<Accordion
						className="px-5 border border-border rounded-xl w-full"
						type="single"
						collapsible
					>
						<AccordionItem value="followers">
							<AccordionTrigger>
								<div className="flex justify-between items-center divide-x divide-border w-full">
									<p className="flex flex-1 items-center gap-2 font-semibold text-base text-secondary">
										Total followers <InfoIcon />
									</p>
									<p className="flex flex-1 justify-center items-center font-semibold text-3xl">
										<div className="w-40 text-left">
											{millify(Number(metircs?.followers.count))}
										</div>
									</p>
								</div>
							</AccordionTrigger>
							<AccordionContent>
								<Separator />
								<div className="flex flex-col">
									<div className="flex items-center py-3 w-full">
										<p className="flex items-center gap-2 w-56 font-semibold text-base text-secondary">
											Pico followers {"[ <1k ]"}
										</p>
										:
										<p className="flex flex-1 justify-center items-center font-semibold text-xl">
											<div className="w-40">
												{millify(Number(metircs?.followers.breakdown.pico))}
											</div>
										</p>
									</div>
									<div className="flex items-center py-3 w-full">
										<p className="flex items-center gap-2 w-56 font-semibold text-base text-secondary">
											Nano followers {"[ 1k-10k ]"}
										</p>
										:
										<p className="flex flex-1 justify-center items-center font-semibold text-xl">
											<div className="w-40">
												{millify(Number(metircs?.followers.breakdown.nano))}
											</div>
										</p>
									</div>{" "}
									<div className="flex items-center py-3 w-full">
										<p className="flex items-center gap-2 w-56 font-semibold text-base text-secondary">
											Micro followers {"[ 10k-50k ]"}
										</p>
										:
										<p className="flex flex-1 justify-center items-center font-semibold text-xl">
											<div className="w-40">
												{millify(Number(metircs?.followers.breakdown.micro))}
											</div>
										</p>
									</div>
									<div className="flex items-center py-3 w-full">
										<p className="flex items-center gap-2 w-56 font-semibold text-base text-secondary">
											MidTier followers {"[ 50k-500k ]"}
										</p>
										:
										<p className="flex flex-1 justify-center items-center font-semibold text-xl">
											<div className="w-40">
												{millify(Number(metircs?.followers.breakdown.midTier))}
											</div>
										</p>
									</div>
									<div className="flex items-center py-3 w-full">
										<p className="flex items-center gap-2 w-56 font-semibold text-base text-secondary">
											Macro followers {"[ 500k-1M ]"}
										</p>
										:
										<p className="flex flex-1 justify-center items-center font-semibold text-xl">
											<div className="w-40">
												{millify(Number(metircs?.followers.breakdown.macro))}
											</div>
										</p>
									</div>
									<div className="flex items-center py-3 w-full">
										<p className="flex items-center gap-2 w-56 font-semibold text-base text-secondary">
											Mega followers {"[ 1M+ ]"}
										</p>
										:
										<p className="flex flex-1 justify-center items-center font-semibold text-xl">
											<div className="w-40">
												{millify(Number(metircs?.followers.breakdown.mega))}
											</div>
										</p>
									</div>
								</div>
							</AccordionContent>
						</AccordionItem>
					</Accordion>

					<Accordion
						className="px-5 border border-border rounded-xl w-full"
						type="single"
						collapsible
						defaultValue="creators"
					>
						<AccordionItem value="creators">
							<AccordionTrigger>
								<div className="flex justify-between items-center divide-x divide-border w-full">
									<p className="flex flex-1 items-center gap-2 font-semibold text-base text-secondary">
										Total count of users <InfoIcon />
									</p>
									<p className="flex flex-1 justify-center items-center font-semibold text-3xl">
										<div className="w-40 text-left">
											{millify(Number(metircs?.creators.count))}
										</div>
									</p>
								</div>
							</AccordionTrigger>
							<AccordionContent>
								<Separator />
								<div className="flex flex-col">
									<div className="flex items-center py-3 w-full">
										<p className="flex items-center gap-2 w-56 font-semibold text-base text-secondary">
											Pico creators {"[ <1k ]"}
										</p>
										:
										<p className="flex flex-1 justify-center items-center font-semibold text-xl">
											<div className="w-40">
												{millify(Number(metircs?.creators.breakdown.pico))}
											</div>
										</p>
									</div>
									<div className="flex items-center py-3 w-full">
										<p className="flex items-center gap-2 w-56 font-semibold text-base text-secondary">
											Nano creators {"[ 1k-10k ]"}
										</p>
										:
										<p className="flex flex-1 justify-center items-center font-semibold text-xl">
											<div className="w-40">
												{millify(Number(metircs?.creators.breakdown.nano))}
											</div>
										</p>
									</div>{" "}
									<div className="flex items-center py-3 w-full">
										<p className="flex items-center gap-2 w-56 font-semibold text-base text-secondary">
											Micro creators {"[ 10k-50k ]"}
										</p>
										:
										<p className="flex flex-1 justify-center items-center font-semibold text-xl">
											<div className="w-40">
												{millify(Number(metircs?.creators.breakdown.micro))}
											</div>
										</p>
									</div>
									<div className="flex items-center py-3 w-full">
										<p className="flex items-center gap-2 w-56 font-semibold text-base text-secondary">
											MidTier creators {"[ 50k-500k ]"}
										</p>
										:
										<p className="flex flex-1 justify-center items-center font-semibold text-xl">
											<div className="w-40">
												{millify(Number(metircs?.creators.breakdown.midTier))}
											</div>
										</p>
									</div>
									<div className="flex items-center py-3 w-full">
										<p className="flex items-center gap-2 w-56 font-semibold text-base text-secondary">
											Macro creators {"[ 500k-1M ]"}
										</p>
										:
										<p className="flex flex-1 justify-center items-center font-semibold text-xl">
											<div className="w-40">
												{millify(Number(metircs?.creators.breakdown.macro))}
											</div>
										</p>
									</div>
									<div className="flex items-center py-3 w-full">
										<p className="flex items-center gap-2 w-56 font-semibold text-base text-secondary">
											Mega creators {"[ 1M+ ]"}
										</p>
										:
										<p className="flex flex-1 justify-center items-center font-semibold text-xl">
											<div className="w-40">
												{millify(Number(metircs?.creators.breakdown.mega))}
											</div>
										</p>
									</div>
								</div>
							</AccordionContent>
						</AccordionItem>
					</Accordion>

					<Accordion
						className="px-5 border border-border rounded-xl w-full"
						type="single"
						collapsible
					>
						<AccordionItem value="views">
							<AccordionTrigger>
								<div className="flex justify-between items-center divide-x divide-border w-full">
									<p className="flex flex-1 items-center gap-2 font-semibold text-base text-secondary">
										Total average view count <InfoIcon />
									</p>
									<p className="flex flex-1 justify-center items-center font-semibold text-3xl">
										<div className="w-40 text-left">
											{millify(Number(metircs?.views.average))}
										</div>
									</p>
								</div>
							</AccordionTrigger>
							<AccordionContent>
								<Separator />
								<div className="flex flex-col">
									<div className="flex items-center py-3 w-full">
										<p className="flex items-center gap-2 w-56 font-semibold text-base text-secondary">
											Pico creators {"[ <1k ]"}
										</p>
										:
										<p className="flex flex-1 justify-center items-center font-semibold text-xl">
											<div className="w-40">
												{millify(Number(metircs?.views.breakdown.pico))}
											</div>
										</p>
									</div>
									<div className="flex items-center py-3 w-full">
										<p className="flex items-center gap-2 w-56 font-semibold text-base text-secondary">
											Nano creators {"[ 1k-10k ]"}
										</p>
										:
										<p className="flex flex-1 justify-center items-center font-semibold text-xl">
											<div className="w-40">
												{millify(Number(metircs?.views.breakdown.nano))}
											</div>
										</p>
									</div>{" "}
									<div className="flex items-center py-3 w-full">
										<p className="flex items-center gap-2 w-56 font-semibold text-base text-secondary">
											Micro creators {"[ 10k-50k ]"}
										</p>
										:
										<p className="flex flex-1 justify-center items-center font-semibold text-xl">
											<div className="w-40">
												{millify(Number(metircs?.views.breakdown.micro))}
											</div>
										</p>
									</div>
									<div className="flex items-center py-3 w-full">
										<p className="flex items-center gap-2 w-56 font-semibold text-base text-secondary">
											MidTier creators {"[ 50k-500k ]"}
										</p>
										:
										<p className="flex flex-1 justify-center items-center font-semibold text-xl">
											<div className="w-40">
												{millify(Number(metircs?.views.breakdown.midTier))}
											</div>
										</p>
									</div>
									<div className="flex items-center py-3 w-full">
										<p className="flex items-center gap-2 w-56 font-semibold text-base text-secondary">
											Macro creators {"[ 500k-1M ]"}
										</p>
										:
										<p className="flex flex-1 justify-center items-center font-semibold text-xl">
											<div className="w-40">
												{millify(Number(metircs?.views.breakdown.macro))}
											</div>
										</p>
									</div>
									<div className="flex items-center py-3 w-full">
										<p className="flex items-center gap-2 w-56 font-semibold text-base text-secondary">
											Mega creators {"[ 1M+ ]"}
										</p>
										:
										<p className="flex flex-1 justify-center items-center font-semibold text-xl">
											<div className="w-40">
												{millify(Number(metircs?.views.breakdown.mega))}
											</div>
										</p>
									</div>
								</div>
							</AccordionContent>
						</AccordionItem>
					</Accordion>

					<Accordion
						className="px-5 border border-border rounded-xl w-full"
						type="single"
						collapsible
					>
						<AccordionItem value="engagement">
							<AccordionTrigger>
								<div className="flex justify-between items-center divide-x divide-border w-full">
									<p className="flex flex-1 items-center gap-2 font-semibold text-base text-secondary">
										Total engagement rate <InfoIcon />
									</p>
									<p className="flex flex-1 justify-center items-center font-semibold text-3xl">
										<div className="flex items-center gap-2 w-40 text-left">
											{millify(Number(metircs?.engagement.average.toFixed(2)))}%{" "}
											<GrowthIcon />
										</div>
									</p>
								</div>
							</AccordionTrigger>
							<AccordionContent>
								<Separator />
								<div className="flex flex-col">
									<div className="flex items-center py-3 w-full">
										<p className="flex items-center gap-2 w-56 font-semibold text-base text-secondary">
											Pico creators {"[ <1k ]"}
										</p>
										:
										<p className="flex flex-1 justify-center items-center font-semibold text-xl">
											<div className="w-40">
												{millify(Number(metircs?.engagement.breakdown.pico))}
											</div>
										</p>
									</div>
									<div className="flex items-center py-3 w-full">
										<p className="flex items-center gap-2 w-56 font-semibold text-base text-secondary">
											Nano creators {"[ 1k-10k ]"}
										</p>
										:
										<p className="flex flex-1 justify-center items-center font-semibold text-xl">
											<div className="w-40">
												{millify(Number(metircs?.engagement.breakdown.nano))}
											</div>
										</p>
									</div>{" "}
									<div className="flex items-center py-3 w-full">
										<p className="flex items-center gap-2 w-56 font-semibold text-base text-secondary">
											Micro creators {"[ 10k-50k ]"}
										</p>
										:
										<p className="flex flex-1 justify-center items-center font-semibold text-xl">
											<div className="w-40">
												{millify(Number(metircs?.engagement.breakdown.micro))}
											</div>
										</p>
									</div>
									<div className="flex items-center py-3 w-full">
										<p className="flex items-center gap-2 w-56 font-semibold text-base text-secondary">
											MidTier creators {"[ 50k-500k ]"}
										</p>
										:
										<p className="flex flex-1 justify-center items-center font-semibold text-xl">
											<div className="w-40">
												{millify(Number(metircs?.engagement.breakdown.midTier))}
											</div>
										</p>
									</div>
									<div className="flex items-center py-3 w-full">
										<p className="flex items-center gap-2 w-56 font-semibold text-base text-secondary">
											Macro creators {"[ 500k-1M ]"}
										</p>
										:
										<p className="flex flex-1 justify-center items-center font-semibold text-xl">
											<div className="w-40">
												{millify(Number(metircs?.engagement.breakdown.macro))}
											</div>
										</p>
									</div>
									<div className="flex items-center py-3 w-full">
										<p className="flex items-center gap-2 w-56 font-semibold text-base text-secondary">
											Mega creators {"[ 1M+ ]"}
										</p>
										:
										<p className="flex flex-1 justify-center items-center font-semibold text-xl">
											<div className="w-40">
												{millify(Number(metircs?.engagement.breakdown.mega))}
											</div>
										</p>
									</div>
								</div>
							</AccordionContent>
						</AccordionItem>
					</Accordion>

					<Accordion
						className="px-5 border border-border rounded-xl w-full"
						type="single"
						collapsible
					>
						<AccordionItem value="genderSplit">
							<AccordionTrigger>
								<div className="flex justify-between items-center divide-x divide-border w-full">
									<p className="flex flex-1 items-center gap-2 font-semibold text-base text-secondary">
										Creators Gender split <InfoIcon />
									</p>
									<p className="flex flex-1 justify-center items-center font-semibold text-3xl">
										<div className="flex items-center gap-2 w-40 text-left">
											{metircs?.genderSplit &&
												millify(
													Number(Object.keys(metircs.genderSplit).length),
												)}
										</div>
									</p>
								</div>
							</AccordionTrigger>
							<AccordionContent>
								<Separator />
								<div className="flex flex-col">
									{metircs?.genderSplit &&
										Object.keys(metircs.genderSplit).map((key) => {
											return (
												<div
													className="flex items-center py-3 w-full"
													key={key}
												>
													<p className="flex items-center gap-2 w-64 font-semibold text-base text-secondary capitalize">
														{key}
													</p>
													:
													<p className="flex flex-1 justify-center items-center font-semibold text-xl">
														<div className="w-40">
															{millify(Number(metircs.genderSplit[key]))}
														</div>
													</p>
												</div>
											);
										})}
								</div>
							</AccordionContent>
						</AccordionItem>
					</Accordion>

					<Accordion
						className="px-5 border border-border rounded-xl w-full"
						type="single"
						collapsible
					>
						<AccordionItem value="genreSplit">
							<AccordionTrigger>
								<div className="flex justify-between items-center divide-x divide-border w-full">
									<p className="flex flex-1 items-center gap-2 font-semibold text-base text-secondary">
										Creators Genre <InfoIcon />
									</p>
									<p className="flex flex-1 justify-center items-center font-semibold text-3xl">
										<div className="flex items-center gap-2 w-40 text-left">
											{metircs?.genderSplit &&
												millify(Number(Object.keys(metircs.genreSplit).length))}
										</div>
									</p>
								</div>
							</AccordionTrigger>
							<AccordionContent>
								<Separator />
								<div className="flex flex-col">
									{metircs?.genreSplit &&
										Object.keys(metircs.genreSplit).map((key) => {
											return (
												<div
													className="flex items-center py-3 w-full"
													key={key}
												>
													<p className="flex items-center gap-2 w-64 font-semibold text-base text-secondary">
														{key} 
													</p>
													:
													<p className="flex flex-1 justify-center items-center font-semibold text-xl">
														<div className="w-40">
															{millify(Number(metircs.genreSplit[key]))}
														</div>
													</p>
												</div>
											);
										})}
								</div>
							</AccordionContent>
						</AccordionItem>
					</Accordion>
				</div>
			)}
		</div>
	);
}
